import React from 'react';
import { Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';

import { JoinStrategy } from '../Sections/Metrics/MetricRow';
import {
  Dimension,
  MetricType,
  MetricWithDimensionFields,
} from '../Sections/Metrics/types';
import Service from '@/lib/App';

export const formatList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const ITERIM_SCORECARD_GENERATION_TIME = 72;

export const calculateScorecardDate = (startDate: string): string => {
  try {
    if (!startDate) {
      return 'date to be determined';
    }

    const date = dayjs(startDate);

    if (!date.isValid()) {
      return 'date to be determined';
    }

    return date
      .add(ITERIM_SCORECARD_GENERATION_TIME, 'hour')
      .format('MMMM D, YYYY');
  } catch (error) {
    return 'date to be determined';
  }
};

export const fetchMetricDimensions = async (
  metricName: string,
  strategy: string,
  joinStrategy?: JoinStrategy,
  assignByFacet?: string,
): Promise<Dimension[]> => {
  try {
    const res = await Service.planner.fetchDimensions(metricName, {
      purpose: 'deepdives',
      strategy,
      joinStrategy,
      assignByFacet,
    });
    return res?.dimensions || [];
  } catch (error) {
    console.error('Error fetching dimensions:', error);
    return [];
  }
};

export const getDimensionsFromAnalysisConfig = (
  rawAnalysisConfig: any,
  metricName: string,
): Dimension[] => {
  if (!rawAnalysisConfig) {
    return [];
  }

  // Search for the metric in all metric types (primary, secondary, guardrail)
  const allMetrics = [
    ...(rawAnalysisConfig.primary_metrics || []),
    ...(rawAnalysisConfig.secondary_metrics || []),
    ...(rawAnalysisConfig.guardrail_metrics || []),
  ];

  const metric = allMetrics.find((m) => m.name === metricName);

  if (!metric || !metric.deepdive_dimensions) {
    return [];
  }

  return metric.deepdive_dimensions.map((d) => ({
    name: d.dimension_name,
    display_name: d.dimension_display_name,
    data_type: 'STRING' as const,
    cardinality: 'LOW' as const,
    governed_dimension_name: d.dimension_name,
  }));
};

// Helper function to get metric type prefix
const getMetricTypePrefix = (metricType: MetricType): string => {
  switch (metricType) {
    case 'primary':
      return '[P0]';
    case 'secondary':
      return '[P1]';
    case 'guardrail':
      return '[G]';
    default:
      return '';
  }
};

export const generateMetricMessage = async (
  deepdiveAllDimensions: boolean,
  strategy: string,
  metricsConfig?: {
    primary_metrics?: MetricWithDimensionFields[];
    secondary_metrics?: MetricWithDimensionFields[];
    guardrail_metrics?: MetricWithDimensionFields[];
  },
  isViewMode?: boolean, // Add flag to determine if in view mode
  rawAnalysisConfig?: any, // Add raw analysis config for view mode
  assignByFacet?: string,
): Promise<React.ReactNode> => {
  // Extract all metrics from metricsConfig if available, otherwise use empty array
  const metrics = metricsConfig
    ? [
        ...(metricsConfig.primary_metrics || []),
        ...(metricsConfig.secondary_metrics || []),
        ...(metricsConfig.guardrail_metrics || []),
      ]
    : [];

  // Function to determine metric type and get prefix
  const getMetricTypeAndPrefix = (
    metric: MetricWithDimensionFields,
  ): string => {
    if (metricsConfig) {
      if (metricsConfig.primary_metrics?.some((m) => m.name === metric.name)) {
        return getMetricTypePrefix('primary');
      }
      if (
        metricsConfig.secondary_metrics?.some((m) => m.name === metric.name)
      ) {
        return getMetricTypePrefix('secondary');
      }
      if (
        metricsConfig.guardrail_metrics?.some((m) => m.name === metric.name)
      ) {
        return getMetricTypePrefix('guardrail');
      }
    }
    return '';
  };

  // Determine the message text based on deepdiveAllDimensions for clarity
  const messageText = deepdiveAllDimensions
    ? 'Experiment scorecard will be generated for the below metrics and dimensions:'
    : 'Experiment scorecard will be generated for the below metrics:';

  if (!deepdiveAllDimensions) {
    return (
      <div>
        <div>{messageText}</div>
        <ul style={{ margin: '8px 0px', paddingLeft: '1.5em' }}>
          {metrics.map((metric) => {
            const prefix = getMetricTypeAndPrefix(metric);
            return (
              <li key={metric.name}>
                <Typography.Text strong style={{ color: 'inherit' }}>
                  {prefix} {metric.metadata?.display_name ?? metric.name}
                </Typography.Text>
              </li>
            );
          })}
        </ul>
      </div>
    );
  } else {
    const deepDivePartsPromises = metrics.map(async (metric) => {
      // Use stored raw analysis config for view mode, otherwise use MIDAS API
      const dimensions =
        isViewMode && rawAnalysisConfig
          ? getDimensionsFromAnalysisConfig(rawAnalysisConfig, metric.name)
          : await fetchMetricDimensions(
              metric.name,
              strategy,
              metric.join_strategy,
              assignByFacet,
            );
      const prefix = getMetricTypeAndPrefix(metric);

      return (
        <li key={metric.name}>
          <Typography.Text strong style={{ color: 'inherit' }}>
            {prefix} {metric.metadata?.display_name ?? metric.name}
          </Typography.Text>
          {dimensions.length > 0 ? (
            ` & its dimension(s): ${formatList(dimensions.map((d) => d.display_name))}`
          ) : (
            <Typography.Text
              type="secondary"
              italic
              style={{ color: 'inherit' }}
            >
              {' (No dimensions found)'}
            </Typography.Text>
          )}
        </li>
      );
    });

    const deepDiveParts = await Promise.all(deepDivePartsPromises);

    return (
      <div>
        <div>{messageText}</div>
        <ul style={{ margin: '8px 0px', paddingLeft: '1.5em' }}>
          {deepDiveParts}
        </ul>
      </div>
    );
  }
};
